import { useSettings } from '@/contexts/settings-context';
import { useState } from 'react';
import { formatFieldValue, getFieldType } from '../../utils';
import {
  getTypeBadgeClasses,
  getTypeColorClasses,
  getTypeFormatClasses,
} from './field-value.domain';

export interface FieldValueProps {
  value: unknown;
  className?: string;
  maxLength?: number;
  onPeek?: (value: unknown, fieldType: 'array' | 'map') => void;
}

export function useFieldValue({ value, className, maxLength = 50, onPeek }: FieldValueProps) {
  const [expanded, setExpanded] = useState(false);
  const { settings } = useSettings();

  const fieldType = getFieldType(value);
  const formatted = formatFieldValue(value);
  const isLong = formatted.length > maxLength;
  const isClickable = Boolean(onPeek && (fieldType === 'array' || fieldType === 'object'));

  const displayValue = expanded || !isLong ? formatted : `${formatted.substring(0, maxLength)}...`;

  const typeFormatClasses = getTypeFormatClasses(fieldType);
  const typeColorClasses = getTypeColorClasses(fieldType);
  const typeBadgeClasses = getTypeBadgeClasses(fieldType);
  const typeDisplayMode = settings.fieldTypeDisplay;

  const showMoreLess = isLong && !isClickable;
  const moreLessLabel = expanded ? 'less' : 'more';

  const handleClick = () => {
    if (isClickable && onPeek) {
      onPeek(value, fieldType === 'array' ? 'array' : 'map');
    }
  };

  const handleMoreLessClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return {
    displayValue,
    fieldType,
    typeFormatClasses,
    typeColorClasses,
    typeBadgeClasses,
    typeDisplayMode,
    isClickable,
    clickTitle: isClickable ? 'Click to view details' : undefined,
    showMoreLess,
    moreLessLabel,
    handleClick,
    handleMoreLessClick,
    className,
  };
}
